import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatMoney, formatApiError, todayISO } from "../lib/api";
import { toast } from "sonner";
import {
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus, Trash2, ArrowRight,
  CheckCircle2, Clock, PlayCircle, X, Save, Flame, Package, PhoneCall, Ban,
} from "lucide-react";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const WEEKDAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

const STATUS_CLR = {
  upcoming: "bg-[#DBEAFE] text-[#1D4ED8]",
  pending:  "bg-[#FEE2E2] text-[#B91C1C]",
  done:     "bg-[#DCFCE7] text-[#15803D]",
  cancelled:"bg-gray-100 text-gray-500 line-through",
};

const iso = (d) => d.toISOString().slice(0, 10);
const parseISO = (s) => new Date(`${s}T00:00:00`);

function daysInMonth(y, m) { return new Date(y, m + 1, 0).getDate(); }
function firstWeekday(y, m) { return new Date(y, m, 1).getDay(); }

function AddModal({ initialDate, brands, sizes, parties, onClose, onSaved, initial }) {
  const [form, setForm] = useState(initial || {
    date: initialDate,
    party_name: "",
    party_id: "",
    phone: "",
    notes: "",
    status: "upcoming",
    items: [{ brand: "", size: "", qty: 0 }],
  });
  const [busy, setBusy] = useState(false);

  const updItem = (i, patch) => setForm((f) => ({ ...f, items: f.items.map((it, k) => k === i ? { ...it, ...patch } : it) }));
  const addRow = () => setForm((f) => ({ ...f, items: [...f.items, { brand: "", size: "", qty: 0 }] }));
  const delRow = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, k) => k !== i) }));

  const save = async () => {
    if (!form.date) { toast.error("Date required"); return; }
    if (!form.party_name.trim()) { toast.error("Party name required"); return; }
    setBusy(true);
    try {
      const payload = { ...form, items: form.items.filter((it) => it.brand && it.size && Number(it.qty) > 0) };
      if (initial?.id) {
        await api.patch(`/scheduled-orders/${initial.id}`, payload);
        toast.success("Order updated");
      } else {
        await api.post("/scheduled-orders", payload);
        toast.success("Scheduled");
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-sm w-full max-w-lg max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="schedule-modal">
        <div className="p-5 border-b flex items-start justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="small-label text-gray-500">{initial ? "Edit" : "New"} scheduled order</div>
            <div className="font-heading font-black text-xl">{form.date}</div>
          </div>
          <button onClick={onClose}><X size={20} /></button>
        </div>

        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="small-label block mb-1">Date</label>
              <input type="date" className="input-lg" value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="sched-date" />
            </div>
            <div>
              <label className="small-label block mb-1">Status</label>
              <select className="input-lg" value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                data-testid="sched-status">
                <option value="upcoming">Upcoming</option>
                <option value="pending">Pending</option>
                <option value="done">Done</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
          <div>
            <label className="small-label block mb-1">Party</label>
            <input list="party-list" className="input-lg" value={form.party_name}
              onChange={(e) => {
                const val = e.target.value;
                const p = parties.find((x) => x.name === val);
                setForm({ ...form, party_name: val, party_id: p?.id || "", phone: p?.phone || form.phone });
              }}
              placeholder="Customer name (type or pick)"
              data-testid="sched-party" />
            <datalist id="party-list">
              {parties.map((p) => <option key={p.id} value={p.name} />)}
            </datalist>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="small-label block mb-1">Phone</label>
              <input className="input-lg" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div>
              <label className="small-label block mb-1">Notes</label>
              <input className="input-lg" value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="e.g. deliver morning" />
            </div>
          </div>

          <div>
            <label className="small-label block mb-2">Items</label>
            <div className="space-y-2">
              {form.items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <select className="input-lg h-9 col-span-4" value={it.brand} onChange={(e) => updItem(i, { brand: e.target.value })} data-testid={`sched-brand-${i}`}>
                    <option value="">Brand</option>
                    {brands.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}
                  </select>
                  <select className="input-lg h-9 col-span-4" value={it.size} onChange={(e) => updItem(i, { size: e.target.value })} data-testid={`sched-size-${i}`}>
                    <option value="">Size</option>
                    {sizes.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
                  </select>
                  <input type="number" min="0" className="input-lg h-9 col-span-3 text-right data-num" value={it.qty}
                    onChange={(e) => updItem(i, { qty: Number(e.target.value) })}
                    placeholder="qty" data-testid={`sched-qty-${i}`} />
                  <button type="button" onClick={() => delRow(i)} className="col-span-1 text-gray-400 hover:text-[#B91C1C]">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              <button type="button" onClick={addRow} className="text-xs font-semibold flex items-center gap-1 hover:underline">
                <Plus size={13} /> Add item
              </button>
            </div>
          </div>
        </div>

        <div className="p-5 border-t sticky bottom-0 bg-white flex justify-end gap-2">
          <button onClick={onClose} className="h-11 px-4 border border-gray-300 rounded-sm font-semibold text-sm">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary h-11 px-5 flex items-center gap-2" data-testid="sched-save">
            <Save size={16} /> {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CalendarView() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [monthData, setMonthData] = useState({ days: [] });
  const [selectedDate, setSelectedDate] = useState(todayISO());
  const [dayData, setDayData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [parties, setParties] = useState([]);
  const [brands, setBrands] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [modal, setModal] = useState(null); // {initialDate} or {initial}

  const loadMonth = async () => {
    const { data } = await api.get("/calendar/month", { params: { year, month: month + 1 } });
    setMonthData(data);
  };
  const loadDay = async (d) => {
    setLoading(true);
    try {
      const { data } = await api.get("/calendar/day", { params: { date: d } });
      setDayData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadMonth(); }, [year, month]);
  useEffect(() => { loadDay(selectedDate); }, [selectedDate]);
  useEffect(() => {
    Promise.all([api.get("/parties?type=customer"), api.get("/brands"), api.get("/sizes")])
      .then(([p, b, s]) => { setParties(p.data); setBrands(b.data); setSizes(s.data); })
      .catch(() => {});
  }, []);

  const daysMap = useMemo(() => {
    const m = {};
    (monthData.days || []).forEach((d) => { m[d.date] = d; });
    return m;
  }, [monthData]);

  const changeMonth = (delta) => {
    let m = month + delta, y = year;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    setMonth(m); setYear(y);
  };
  const today = todayISO();

  // Overall KPIs (this month + upcoming)
  const monthKpis = useMemo(() => {
    const list = monthData.days || [];
    return {
      sales_count: list.reduce((s, d) => s + (d.sales_count || 0), 0),
      sales_qty: list.reduce((s, d) => s + (d.sales_qty || 0), 0),
      sales_kg: list.reduce((s, d) => s + (d.sales_kg || 0), 0),
      scheduled: list.reduce((s, d) => s + (d.scheduled_count || 0), 0),
      pending: list.reduce((s, d) => s + (d.pending_count || 0), 0),
    };
  }, [monthData]);

  const postpone = async (id) => {
    try { await api.post(`/scheduled-orders/${id}/postpone`); toast.success("Postponed to next day"); loadDay(selectedDate); loadMonth(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const markStatus = async (id, status) => {
    try { await api.patch(`/scheduled-orders/${id}`, { status }); toast.success(status); loadDay(selectedDate); loadMonth(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this scheduled order?")) return;
    try { await api.delete(`/scheduled-orders/${id}`); toast.success("Deleted"); loadDay(selectedDate); loadMonth(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // Build calendar grid
  const dim = daysInMonth(year, month);
  const start = firstWeekday(year, month);
  const cells = [];
  for (let i = 0; i < start; i++) cells.push(null);
  for (let d = 1; d <= dim; d++) cells.push(new Date(year, month, d));

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <CalendarIcon size={20} className="text-black" />
          <h2 className="font-heading font-black text-2xl tracking-tight">Calendar</h2>
        </div>
        <span className="text-xs text-gray-500 border border-gray-200 rounded-sm px-2 py-0.5">
          Delivered orders (from ledger) + scheduled/pending orders (planning only)
        </span>
        <button onClick={() => setModal({ initialDate: selectedDate })}
          className="ml-auto btn-primary h-9 px-4 flex items-center gap-2" data-testid="new-schedule">
          <Plus size={14} /> New scheduled order
        </button>
      </div>

      {/* Monthly KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="card-flat p-5 bg-black text-white border-black">
          <div className="small-label text-white/70 flex items-center gap-1.5"><Flame size={12} /> Sold this month (KG)</div>
          <div className="data-num text-3xl mt-1">{formatMoney(Math.round(monthKpis.sales_kg * 100) / 100)}</div>
        </div>
        <div className="card-flat p-5">
          <div className="small-label text-gray-500 flex items-center gap-1.5"><Package size={12} /> Cylinders sold</div>
          <div className="data-num text-3xl mt-1">{monthKpis.sales_qty}</div>
        </div>
        <div className="card-flat p-5">
          <div className="small-label text-gray-500">Sale entries</div>
          <div className="data-num text-3xl mt-1">{monthKpis.sales_count}</div>
        </div>
        <div className="card-flat p-5 bg-[#DBEAFE] border-[#BFDBFE]">
          <div className="small-label text-[#1D4ED8] flex items-center gap-1.5"><Clock size={12} /> Scheduled</div>
          <div className="data-num text-3xl mt-1 text-[#1D4ED8]">{monthKpis.scheduled}</div>
        </div>
        <div className="card-flat p-5 bg-[#FEE2E2] border-[#FECACA]">
          <div className="small-label text-[#B91C1C] flex items-center gap-1.5"><Clock size={12} /> Pending (postponed)</div>
          <div className="data-num text-3xl mt-1 text-[#B91C1C]">{monthKpis.pending}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar grid */}
        <div className="lg:col-span-2 card-flat">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <button onClick={() => changeMonth(-1)} className="h-9 w-9 flex items-center justify-center border border-gray-300 rounded-sm hover:border-black" data-testid="prev-month">
              <ChevronLeft size={16} />
            </button>
            <div className="font-heading font-black text-xl">{MONTHS[month]} {year}</div>
            <button onClick={() => changeMonth(1)} className="h-9 w-9 flex items-center justify-center border border-gray-300 rounded-sm hover:border-black" data-testid="next-month">
              <ChevronRight size={16} />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-px bg-gray-200 border border-gray-200">
            {WEEKDAYS.map((w) => (
              <div key={w} className="bg-white p-2 text-center small-label text-gray-500">{w}</div>
            ))}
            {cells.map((d, i) => {
              if (!d) return <div key={i} className="bg-gray-50 min-h-[70px]" />;
              const ds = iso(d);
              const info = daysMap[ds] || {};
              const isToday = ds === today;
              const isSel = ds === selectedDate;
              return (
                <button
                  key={ds}
                  onClick={() => setSelectedDate(ds)}
                  className={`bg-white p-2 min-h-[70px] text-left flex flex-col gap-0.5 hover:bg-[#FEF3C7] transition-colors ${isSel ? "outline outline-2 outline-black" : ""} ${isToday ? "bg-[#FEF3C7]/40" : ""}`}
                  data-testid={`day-${ds}`}
                >
                  <div className={`text-xs font-bold ${isToday ? "text-[#B45309]" : ""}`}>{d.getDate()}</div>
                  {info.sales_count > 0 && (
                    <div className="text-[10px] text-[#15803D] font-semibold">{info.sales_count}× sale · {info.sales_qty}c</div>
                  )}
                  {info.scheduled_count > 0 && (
                    <div className="text-[10px] text-[#1D4ED8] font-semibold">{info.scheduled_count} scheduled</div>
                  )}
                  {info.pending_count > 0 && (
                    <div className="text-[10px] text-[#B91C1C] font-semibold">{info.pending_count} pending</div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Day panel */}
        <div className="card-flat">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <div className="small-label text-gray-500">Selected day</div>
              <div className="font-heading font-black text-xl">
                {parseISO(selectedDate).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "short", year: "numeric" })}
              </div>
            </div>
          </div>
          <div className="p-4 space-y-4 max-h-[70vh] overflow-auto">
            {loading && <div className="text-sm text-gray-400 text-center py-6">Loading…</div>}

            {/* Sales section */}
            {dayData && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="small-label text-[#15803D] flex items-center gap-1.5"><CheckCircle2 size={12} /> Delivered ({dayData.sales.length})</div>
                  {dayData.totals.sales_qty_cyl > 0 && (
                    <div className="text-xs text-gray-500">
                      <span className="data-num font-bold text-black">{dayData.totals.sales_qty_cyl}</span> cyl · <span className="data-num font-bold text-black">{formatMoney(dayData.totals.sales_qty_kg)}</span> KG · <span className="data-num font-bold text-black">₹ {formatMoney(dayData.totals.sales_amount)}</span>
                    </div>
                  )}
                </div>
                {dayData.sales.length === 0 && <div className="text-xs text-gray-400 italic mb-3">No sales delivered on this day.</div>}
                <div className="space-y-1.5">
                  {dayData.sales.map((s) => (
                    <div key={s.id} className="border border-gray-200 rounded-sm p-2.5 text-sm" data-testid={`day-sale-${s.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-semibold truncate">
                            {s.party_id
                              ? <Link to={`/parties/${s.party_id}`} className="hover:underline">{s.party_name}</Link>
                              : s.party_name || "—"}
                          </div>
                          <div className="text-xs text-gray-500">CH {s.challan_no || "—"}{s.vehicle_no ? ` · ${s.vehicle_no}` : ""}</div>
                          <div className="text-xs mt-0.5">
                            {s.items.map((it, i) => <span key={i} className="inline-block bg-gray-100 rounded-sm px-1.5 py-0.5 mr-1 mb-0.5">{it.brand} {it.size}× {it.filled_qty}</span>)}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="data-num text-sm font-bold">{s.qty_kg} KG</div>
                          <div className="text-xs text-gray-500">₹ {formatMoney(s.amount)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Scheduled section */}
            {dayData && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="small-label text-[#1D4ED8] flex items-center gap-1.5"><Clock size={12} /> Scheduled / pending ({dayData.scheduled.length})</div>
                  <button onClick={() => setModal({ initialDate: selectedDate })} className="text-xs font-semibold text-black hover:underline flex items-center gap-1">
                    <Plus size={12} /> Add
                  </button>
                </div>
                {dayData.scheduled.length === 0 && <div className="text-xs text-gray-400 italic mb-3">Nothing scheduled.</div>}
                <div className="space-y-1.5">
                  {dayData.scheduled.map((s) => (
                    <div key={s.id} className="border border-gray-200 rounded-sm p-2.5" data-testid={`sched-item-${s.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className={`px-1.5 py-0.5 text-[10px] rounded-sm font-semibold ${STATUS_CLR[s.status] || "bg-gray-100"}`}>{s.status}</span>
                            <div className="font-semibold text-sm truncate">{s.party_name}</div>
                          </div>
                          {s.phone && <div className="text-xs text-gray-500">{s.phone}</div>}
                          <div className="text-xs mt-0.5">
                            {(s.items || []).map((it, i) => <span key={i} className="inline-block bg-gray-100 rounded-sm px-1.5 py-0.5 mr-1 mb-0.5">{it.brand} {it.size}× {it.qty}</span>)}
                          </div>
                          {s.notes && <div className="text-xs text-gray-500 mt-1">{s.notes}</div>}
                        </div>
                        <div className="flex flex-col gap-1 shrink-0">
                          {s.status !== "done" && (
                            <button onClick={() => markStatus(s.id, "done")} className="text-[11px] px-2 py-1 rounded-sm border border-gray-200 hover:bg-[#DCFCE7] hover:text-[#15803D] flex items-center gap-1"
                              data-testid={`sched-done-${s.id}`}>
                              <CheckCircle2 size={11} /> Done
                            </button>
                          )}
                          {s.status !== "cancelled" && s.status !== "done" && (
                            <button onClick={() => postpone(s.id)} className="text-[11px] px-2 py-1 rounded-sm border border-gray-200 hover:bg-[#FEF3C7] hover:text-[#B45309] flex items-center gap-1"
                              data-testid={`sched-postpone-${s.id}`}>
                              <ArrowRight size={11} /> +1 day
                            </button>
                          )}
                          <button onClick={() => setModal({ initial: s })} className="text-[11px] px-2 py-1 rounded-sm border border-gray-200 hover:border-black flex items-center gap-1">
                            <PlayCircle size={11} /> Edit
                          </button>
                          <button onClick={() => del(s.id)} className="text-[11px] px-2 py-1 rounded-sm border border-gray-200 hover:bg-[#FEE2E2] hover:text-[#B91C1C] flex items-center gap-1"
                            data-testid={`sched-del-${s.id}`}>
                            <Trash2 size={11} /> Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {modal && (
        <AddModal
          initialDate={modal.initialDate}
          initial={modal.initial}
          brands={brands}
          sizes={sizes}
          parties={parties}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); loadDay(selectedDate); loadMonth(); }}
        />
      )}
      <PhoneCall size={0} /><Ban size={0} />
    </div>
  );
}
